import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import useCartInfo from "@repo/shopify/hooks/useCartInfo";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import useTracking from "@repo/shopify/hooks/useTracking";
import { extractId } from "@repo/shopify/utils/common";
import classNames from "classnames";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "@atoms/Button";
import Link from "@atoms/Link";
import Price from "@atoms/Price";

import CartLineItem from "@molecules/CartLineItem";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useCartQuery from "@hooks/useCartQuery";
import useCheckPurchaseLimit from "@hooks/useCheckPurchaseLimit";
import useLanguage from "@hooks/useLanguage";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

export default function HeaderCart() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { t } = useTranslation();
  const { isPC } = useCheckDevice();
  const { lang } = useLanguage();

  const { token, caaToken, customer } = useCustomerStore();
  const hasCustomerSession = lang === "jp" ? Boolean(caaToken?.accessToken) : Boolean(token.accessToken);
  const { cart } = useCartStore();
  const { openCart, openToast, openAccount, openModal } = useLayoutStore();
  const { updateCartLine, removeCartLine, updateCartLineLoading, removeCartLineLoading, addCartLineLoading } =
    useCartQuery();

  const { remainingLimit, expectedRemainingLimit } = useCheckPurchaseLimit();

  const { gtmBeginCheckout } = useTracking();

  const [height, setHeight] = useState<number>(0);

  const ref = useRef<HTMLDivElement | null>(null);

  const { totalQuantity, subTotalAmount } = useCartInfo(cart);

  // 카트 수량 변경 또는 사은품 처리 중인지 확인
  const isCartUpdating = useMemo(
    () => updateCartLineLoading || removeCartLineLoading || addCartLineLoading,
    [updateCartLineLoading, removeCartLineLoading, addCartLineLoading],
  );

  useEffect(() => {
    ChannelService.track("openCart");
  }, []);

  const handleCheckout = () => {
    sessionStorage.setItem("checkoutBackUrl", pathname);

    if (!hasCustomerSession) {
      openToast(NOTIFICATIONS.WARNING_NO_LOGIN);
      openAccount(true);
      openCart(false);
      sessionStorage.setItem("loginAfterUrl", URLS.CHECKOUT);
      return;
    }

    if (customer.tags.includes("PARTNERSHIP_TOSS")) {
      if (remainingLimit <= 0 || expectedRemainingLimit < 0) {
        openModal({
          title: "WARNING",
          content: (
            <p>
              {t("warning.exceed_purchase_limit")}
              <br />
              <Trans i18nKey="warning.remaining_limit" values={{ amount: remainingLimit.toLocaleString() }} />
            </p>
          ),
          confirm: {
            label: "Okay",
            onClick: () => {
              openModal(null);
            },
          },
        });

        return;
      }
    }

    gtmBeginCheckout(cart, extractId(customer.id), customer.email ?? undefined);

    openCart(false);

    // JP는 커스텀 체크아웃 대신 Shopify 기본 체크아웃으로 바로 이동
    if (lang === "jp") {
      if (cart.checkoutUrl) window.location.href = cart.checkoutUrl;
      return;
    }

    navigate(URLS.CHECKOUT);
  };

  useLayoutEffect(() => {
    if (ref.current) {
      setHeight(ref.current.clientHeight || 0);
    }
  }, [ref]);

  return (
    <div
      className={classNames(
        "pc:right-0 pc:h-auto pc:max-h-dvh pc:w-1/3 pc:px-5 pc:pb-5 pc:z-60 fixed z-30 flex h-dvh w-full flex-col gap-2.5 bg-white px-4 pb-4 lg:w-1/4 dark:bg-black dark:text-white",
        !isPC ? [cart?.lines.edges.some(({ node }) => node.quantity > 0) ? "top-0" : "top-13.5"] : "top-0",
      )}
    >
      {/* Cart Title */}
      {isPC ? (
        <div className="absolute top-5 z-30 flex w-[calc(100%-2.5rem)] justify-between">
          <span className="col-span-8 uppercase">
            {totalQuantity > 0 ? `Shopping Cart - ${totalQuantity} items` : "Empty Cart"}
          </span>
          <Button
            as="text"
            onClick={() => {
              openCart(false);
            }}
          >
            Close
          </Button>
        </div>
      ) : null}

      <div className="pc:max-h-[calc(100dvh-1.25rem)] pc:h-fit flex h-[calc(100dvh-1.25rem)] flex-col gap-2.5 bg-white dark:bg-black">
        {cart.lines.edges.some(({ node }) => node.quantity > 0) ? (
          <>
            {/* LineItems */}
            <div
              className="scrollbar-hide flex max-h-dvh flex-col gap-10 overflow-y-scroll pt-17.5"
              style={{ paddingBottom: `calc(${height}px + 10px)` }}
            >
              {/*Gift가 아닌 상품 먼저 보여준다*/}
              {cart.lines.edges
                ?.filter(({ node }) => !node.merchandise.product.tags.includes("Gift") && node.quantity > 0)
                .map(({ node }) => (
                  <CartLineItem
                    key={node.id}
                    node={node}
                    removeCartLine={removeCartLine}
                    removeCartLineLoading={removeCartLineLoading}
                    updateCartLine={updateCartLine}
                    updateCartLineLoading={updateCartLineLoading}
                  />
                ))}
              {/*Gift 상품은 일반 상품 뒤에 보여준다*/}
              {cart.lines.edges
                ?.filter(({ node }) => node.merchandise.product.tags.includes("Gift") && node.quantity > 0)
                .map(({ node }) => (
                  <CartLineItem
                    key={node.id}
                    node={node}
                    removeCartLine={removeCartLine}
                    removeCartLineLoading={removeCartLineLoading}
                    updateCartLine={updateCartLine}
                    updateCartLineLoading={updateCartLineLoading}
                  />
                ))}
            </div>

            <div
              className="pc:bottom-5 pc:pt-5 pc:px-5 absolute bottom-4 left-0 z-20 flex w-full flex-col gap-5 bg-white px-4 pt-4 dark:bg-black"
              ref={ref}
            >
              <div className="flex gap-2.5">
                <span className="text-right uppercase">Subtotal</span>
                <div className="flex w-full flex-col gap-1">
                  <Price price={subTotalAmount} />
                  <p className="text-sm font-normal">{t("cart.discount")}</p>
                </div>
              </div>
              <Button as="square" className="w-full justify-center!" disabled={isCartUpdating} onClick={handleCheckout}>
                Checkout Now
              </Button>
            </div>
          </>
        ) : (
          <div className="pc:pt-13.5 flex flex-col gap-4 pt-4">
            {!isPC && <span>No items in your cart</span>}
            <Link as="square" className="flex! w-full justify-center!" href={URLS.COLLECTIONS_ALL}>
              Continue Shopping
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
